import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, auth } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Settings, Phone, MapPin, CheckCircle, Eye, Star } from "lucide-react";
import WhatsAppButton from "@/components/WhatsAppButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PartnerDashboard() {
  const [profile, setProfile] = useState(null);
  const [leads, setLeads] = useState([]);
  const [services, setServices] = useState([]);

  const load = async () => {
    const [p, l, s] = await Promise.all([
      api.get("/partners/me"),
      api.get("/partners/leads"),
      api.get("/services"),
    ]);
    setProfile(p.data);
    setLeads(l.data);
    setServices(s.data);
    auth.setSession(auth.getToken(), "partner", p.data);
  };

  useEffect(() => {
    load();
  }, []);

  const respond = async (leadId) => {
    try {
      await api.post(`/partners/leads/${leadId}/respond`);
      toast.success("Customer details unlocked");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const serviceName = (id) => services.find((s) => s.id === id)?.name || id;

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Navbar variant="dashboard" />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#E06D53] font-semibold">
              Partner Dashboard
            </div>
            <h1 className="font-display text-4xl mt-2">
              {profile?.name || "Partner"}
            </h1>
            <p className="text-[#4A4A4A] mt-2">
              Pincode {profile?.service_pincode} · Radius {profile?.service_radius_km} km
            </p>
            {profile?.review_count > 0 && (
              <div className="mt-2 inline-flex items-center gap-1" data-testid="partner-rating-badge">
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star key={n} className={`w-4 h-4 ${n <= Math.round(profile.avg_rating || 0) ? "fill-[#E06D53] text-[#E06D53]" : "text-[#E5E3DB]"}`} />
                ))}
                <span className="ml-1 text-sm font-semibold">{profile.avg_rating}</span>
                <span className="text-xs text-[#737373]">({profile.review_count})</span>
              </div>
            )}
          </div>
          <Link
            to="/partner/settings"
            className="h-12 px-5 rounded-full border border-[#E5E3DB] inline-flex items-center gap-2 font-semibold hover:bg-white"
            data-testid="partner-settings-link"
          >
            <Settings className="w-4 h-4" /> Account Settings
          </Link>
        </div>

        {/* Profile summary - control room style */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white border border-[#E5E3DB] rounded-xl p-4" data-testid="stat-services">
            <div className="text-xs text-[#737373] uppercase tracking-widest font-semibold">Services</div>
            <div className="font-display text-2xl mt-2">{profile?.services?.length || 0}</div>
          </div>
          <div className="bg-white border border-[#E5E3DB] rounded-xl p-4" data-testid="stat-leads-total">
            <div className="text-xs text-[#737373] uppercase tracking-widest font-semibold">Total Leads</div>
            <div className="font-display text-2xl mt-2">{leads.length}</div>
          </div>
          <div className="bg-white border border-[#E5E3DB] rounded-xl p-4" data-testid="stat-leads-responded">
            <div className="text-xs text-[#737373] uppercase tracking-widest font-semibold">Responded</div>
            <div className="font-display text-2xl mt-2">{leads.filter(l => l.has_responded).length}</div>
          </div>
          <div className="bg-white border border-[#E5E3DB] rounded-xl p-4" data-testid="stat-leads-chosen">
            <div className="text-xs text-[#737373] uppercase tracking-widest font-semibold">Assigned to me</div>
            <div className="font-display text-2xl mt-2">{leads.filter(l => l.is_chosen).length}</div>
          </div>
        </div>

        {/* Service tags */}
        {profile?.services && (
          <div className="mt-6 flex flex-wrap gap-2">
            {profile.services.map((sid) => (
              <span key={sid} className="px-3 py-1 rounded-full bg-[#1E3F20] text-white text-xs font-semibold">
                {serviceName(sid)}
              </span>
            ))}
          </div>
        )}

        {/* Leads */}
        <div className="mt-10">
          <div className="text-sm font-semibold text-[#737373] uppercase tracking-widest mb-4">
            Leads in your area
          </div>
          {leads.length === 0 ? (
            <div className="card-octa p-10 text-center" data-testid="partner-no-leads">
              <div className="font-display text-2xl">No leads yet</div>
              <p className="text-[#737373] mt-2 text-sm">
                New leads matching your services and pincode will appear here.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {leads.map((lead) => (
                <div
                  key={lead.id}
                  className={`bg-white border rounded-xl p-6 ${lead.is_chosen ? "border-[#1E3F20]" : "border-[#E5E3DB]"}`}
                  data-testid={`partner-lead-${lead.id}`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <div className="font-display-medium text-xl">
                          {lead.service_name}
                        </div>
                        {lead.is_chosen && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-[#1E3F20] text-white text-xs font-semibold">
                            <CheckCircle className="w-3 h-3" /> You are chosen
                          </span>
                        )}
                        {lead.has_responded && !lead.is_chosen && (
                          <span className="px-2.5 py-0.5 rounded-full bg-[#F3F1ED] text-[#1A1A1A] text-xs font-semibold">
                            Responded
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-[#4A4A4A] mt-2 max-w-2xl">
                        {lead.description}
                      </p>

                      {lead.tutor_details && (
                        <div className="mt-3 inline-flex flex-wrap gap-2" data-testid={`tutor-info-${lead.id}`}>
                          {lead.tutor_details.cls && (
                            <span className="px-2.5 py-1 rounded-full bg-[#1E3F20] text-white text-xs font-semibold">
                              {lead.tutor_details.cls}
                            </span>
                          )}
                          {(lead.tutor_details.subjects || []).map((s) => (
                            <span key={s} className="px-2.5 py-1 rounded-full bg-[#F3F1ED] border border-[#E5E3DB] text-[#1A1A1A] text-xs font-medium">
                              {s}
                            </span>
                          ))}
                          {lead.tutor_details.area_location && (
                            <span className="px-2.5 py-1 rounded-full bg-white border border-[#E5E3DB] text-[#737373] text-xs">
                              📍 {lead.tutor_details.area_location}
                            </span>
                          )}
                        </div>
                      )}

                      {lead.extra_details && Object.keys(lead.extra_details).length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2" data-testid={`extra-info-${lead.id}`}>
                          {Object.entries(lead.extra_details).map(([k, v]) => {
                            const display = Array.isArray(v) ? v.join(", ") : v;
                            if (!display) return null;
                            return (
                              <span
                                key={k}
                                className="px-2.5 py-1 rounded-full bg-[#F3F1ED] border border-[#E5E3DB] text-[#1A1A1A] text-xs"
                              >
                                <span className="text-[#737373] font-medium">{k.replace(/_/g, " ")}:</span>{" "}
                                <strong>{display}</strong>
                              </span>
                            );
                          })}
                        </div>
                      )}

                      {(lead.images || []).length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2" data-testid={`lead-images-${lead.id}`}>
                          {lead.images.map((fname) => (
                            <a key={fname} href={`${BACKEND_URL}/api/files/${fname}`} target="_blank" rel="noreferrer" className="w-16 h-16 rounded-lg overflow-hidden border border-[#E5E3DB] block">
                              <img src={`${BACKEND_URL}/api/files/${fname}`} alt="" className="w-full h-full object-cover" />
                            </a>
                          ))}
                        </div>
                      )}

                      {lead.review && (
                        <div className="mt-3 p-3 rounded-xl bg-[#F3F1ED] border border-[#E5E3DB]" data-testid={`partner-review-${lead.id}`}>
                          <div className="flex items-center gap-1">
                            {[1, 2, 3, 4, 5].map((n) => (
                              <Star key={n} className={`w-3.5 h-3.5 ${n <= lead.review.rating ? "fill-[#E06D53] text-[#E06D53]" : "text-[#E5E3DB]"}`} />
                            ))}
                            <span className="ml-1 text-xs text-[#737373]">Customer review</span>
                          </div>
                          {lead.review.comment && <p className="text-xs text-[#4A4A4A] mt-1">{lead.review.comment}</p>}
                        </div>
                      )}

                      <div className="flex flex-wrap gap-4 mt-3 text-xs text-[#737373]">
                        <span><MapPin className="w-3 h-3 inline mr-1" /> Pincode {lead.pincode}</span>
                        {lead.preferred_time && <span>⏱ {lead.preferred_time}</span>}
                        <span>{new Date(lead.created_at).toLocaleString()}</span>
                      </div>

                      {(lead.has_responded || lead.is_chosen) ? (
                        <div className="mt-4 p-4 rounded-xl bg-[#F3F1ED] border border-[#E5E3DB]">
                          <div className="text-xs uppercase tracking-widest text-[#1E3F20] font-semibold">
                            Customer details
                          </div>
                          <div className="mt-2 font-display-medium">{lead.customer_name}</div>
                          <div className="text-sm flex items-center gap-2 mt-1">
                            <Phone className="w-3.5 h-3.5" />
                            <a href={`tel:${lead.customer_mobile}`} className="font-semibold text-[#1E3F20]" data-testid={`call-customer-${lead.id}`}>
                              {lead.customer_mobile}
                            </a>
                          </div>
                          <div className="text-sm text-[#4A4A4A] mt-1">
                            {lead.customer_address}
                          </div>
                          <div className="mt-3">
                            <WhatsAppButton
                              phone={lead.customer_mobile}
                              text={`Hi ${lead.customer_name?.split(" ")[0] || "there"}, this is your ${lead.service_name} partner from OCTA Services. When would be a good time to reach you?`}
                              label="WhatsApp customer"
                              testId={`whatsapp-customer-${lead.id}`}
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="mt-4 text-sm text-[#737373]">
                          Customer: <strong>{lead.customer_name}</strong> · Tap respond to see contact details
                        </div>
                      )}
                    </div>

                    {!lead.has_responded && !lead.is_chosen && (
                      <button
                        onClick={() => respond(lead.id)}
                        className="h-10 px-4 rounded-full btn-accent font-semibold inline-flex items-center gap-2"
                        data-testid={`respond-lead-${lead.id}`}
                      >
                        <Eye className="w-4 h-4" /> Respond
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
